/**
 * Logical size for ranking PNG exports (4:5 portrait).
 * Capture paints at 4× and the PNG is downscaled back to this size.
 */
export const RANKING_ASSET_WIDTH_PX = 1080;
export const RANKING_ASSET_HEIGHT_PX = 1350;
/** Card shell padding (matches RadarPodscopeRankingCard screenshot layout). */
export const RANKING_ASSET_TOP_PADDING_PX = 96;
export const RANKING_ASSET_BOTTOM_PADDING_PX = 48;
export const RANKING_ASSET_OUTER_PADDING_X_PX = 48;
/** Extra inset for ranking rows / livestream graph block inside the shell. */
export const RANKING_ASSET_CONTENT_INSET_X_PX = 72;
/** Subheader bottom → period pill top on ranking assets (views week layout). */
export const RANKING_ASSET_HEADER_TO_PILL_GAP_PX = 60;
/** Period pill block height (py + line + border). */
export const RANKING_ASSET_PERIOD_PILL_BLOCK_HEIGHT_PX = 48;
/** Period pill bottom → main content top on ranking assets. */
export const RANKING_ASSET_PILL_TO_CONTENT_GAP_PX = 72;
/** Vertical reserve when the period pill is hidden but layout spacing is preserved. */
export const RANKING_ASSET_HIDDEN_PERIOD_PILL_SPACER_PX =
  RANKING_ASSET_HEADER_TO_PILL_GAP_PX +
  RANKING_ASSET_PERIOD_PILL_BLOCK_HEIGHT_PX +
  RANKING_ASSET_PILL_TO_CONTENT_GAP_PX;
/** Program thumb size on ranking rows. */
export const RANKING_THUMB_PX = 100;
/** Cover corner radius at `RANKING_THUMB_PX`; smaller thumbs scale this. */
export const RANKING_THUMB_RADIUS_PX = 20;

/** Cover radius for a thumb rendered at `thumbPx` (50px livestream thumbs → 10px). */
export const rankingThumbRadius = (
  thumbPx: number = RANKING_THUMB_PX,
): number => RANKING_THUMB_RADIUS_PX * (thumbPx / RANKING_THUMB_PX);

/**
 * Inner padding on ranking row cards (`rowBoxSx`).
 * Card-to-card gap uses the same value so gutters match inset.
 */
export const RANKING_ASSET_ROW_PADDING_X_PX = 20;
export const RANKING_ASSET_ROW_PADDING_Y_PX = RANKING_ASSET_ROW_PADDING_X_PX;
/** Gap between ranking row cards (and grid columns) — same as card padding. */
export const RANKING_ASSET_ROW_GAP_PX = RANKING_ASSET_ROW_PADDING_X_PX;
/** Gap inside a ranking row (thumb ↔ text). */
export const RANKING_ASSET_ROW_INNER_GAP_PX = 12;
/** Row height: thumb + equal padding above/below. */
export const RANKING_ASSET_ROW_HEIGHT_PX =
  RANKING_THUMB_PX + 2 * RANKING_ASSET_ROW_PADDING_Y_PX;
/** Border radius on ranking row cards (`rowBoxSx`). */
export const RANKING_ASSET_ROW_BORDER_RADIUS_PX = 20;

/** Ranking grid: 5 stacked rows per column. */
export const RANKING_ASSET_ROWS_PER_COLUMN = 5;
/** Height of the two-column rows block (rows + gaps between them). */
export const RANKING_ASSET_ROWS_BLOCK_HEIGHT_PX =
  RANKING_ASSET_ROWS_PER_COLUMN * RANKING_ASSET_ROW_HEIGHT_PX +
  (RANKING_ASSET_ROWS_PER_COLUMN - 1) * RANKING_ASSET_ROW_GAP_PX;
/**
 * Space between the period pill and the footer on the 1350px canvas. Row
 * padding feeds both row height and gaps, so it grows the block 14x — past
 * this the PNG clips rows instead of overflowing visibly.
 */
export const RANKING_ASSET_ROWS_BLOCK_MAX_HEIGHT_PX = 830;
/** Podcast badge edge length (~1/3 of thumb; integer to avoid subpixel drift). */
export const RANKING_PODCAST_BADGE_SIZE_PX = 34;
/** How far the podcast badge overhangs the program thumb (left + bottom). */
export const RANKING_PODCAST_BADGE_OVERFLOW_PX = 8;
/** Transparent gap punched evenly around the podcast badge. */
export const RANKING_PODCAST_BADGE_GAP_PX = 3;
/** Podcast badge corner radius. */
export const RANKING_PODCAST_BADGE_RADIUS_PX = 12;
